import gql from 'graphql-tag';

import { Query } from 'core/index';
import { USER_ERROR_FIELDS } from 'core/errors';

import { PROJECT_CONNECTION_FIELDS } from 'apps/project/gql';
import { ISSUE_FIELDS } from 'apps/issueTracker/gql';
import {
  PROFILE_THREAD_CONNECTION_FIELDS,
  PROFILE_CONNECTION_FIELDS,
} from 'apps/profile/gql';
import { INITIATIVE_CONNECTION_FIELDS } from 'apps/initiative/gql/fragments';
import { STORY_CONNECTION_FIELDS } from 'apps/story/gql';

export const PROJECTS = gql`
  ${PROJECT_CONNECTION_FIELDS}
  ${USER_ERROR_FIELDS}
  query WhiteLabelProjects(
    $batchSize: Int
    $cursor: String
    $sortBy: String
    $contains: [String!]
    $notContains: [[String]]
    $filterBy: [[String!]]
    $whitelabels: [String]
  ) {
    projects(
      first: $batchSize
      after: $cursor
      sortBy: $sortBy
      contains: $contains
      notContains: $notContains
      filterBy: $filterBy
      whitelabels: $whitelabels
    ) {
      result {
        ...projectConnectionFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;
export class WhiteLabelProjectsQuery extends Query {
  static conf = {
    GQL: PROJECTS,
    varNames: [
      'batchSize',
      'cursor',
      'sortBy',
      'contains',
      'notContains',
      'filterBy',
      'whitelabels',
    ],
    resultPath: 'projects.result',
    errorPath: 'projects.userErrors',
  };
}

const ISSUES = gql`
  ${ISSUE_FIELDS}
  ${USER_ERROR_FIELDS}
  query Issues(
    $batchSize: Int
    $cursor: String
    $sortBy: String
    $filterBy: [[String!]]
    $whitelabels: [String]
  ) {
    issues(
      first: $batchSize
      after: $cursor
      sortBy: $sortBy
      filterBy: $filterBy
      whitelabels: $whitelabels
    ) {
      result {
        pageInfo {
          startCursor
          endCursor
          hasNextPage
        }
        totalCount
        edges {
          cursor
          node {
            ...issueFields
          }
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class WhiteLabelIssuesQuery extends Query {
  static conf = {
    GQL: ISSUES,
    varNames: ['batchSize', 'cursor', 'sortBy', 'filterBy', 'whitelabels'],
    resultPath: 'issues.result',
    errorPath: 'issues.userErrors',
  };
}

// TODO remove once we can do beter filtering and then do
// api_tracker.viralresponse_query_filter_project_tag based on args, not as new top level
const VIRAL_ISSUES = gql`
  ${ISSUE_FIELDS}
  ${USER_ERROR_FIELDS}
  query ViralIssues(
    $batchSize: Int
    $cursor: String
    $sortBy: String
    $contains: [String!]
    $filterBy: [[String!]]
  ) {
    viralresponseIssues(
      first: $batchSize
      after: $cursor
      sortBy: $sortBy
      contains: $contains
      filterBy: $filterBy
    ) {
      result {
        pageInfo {
          startCursor
          endCursor
          hasNextPage
        }
        totalCount
        edges {
          cursor
          node {
            ...issueFields
          }
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ViralIssuesQuery extends Query {
  static conf = {
    GQL: VIRAL_ISSUES,
    varNames: ['batchSize', 'cursor', 'sortBy', 'filterBy', 'contains'],
    resultPath: 'viralresponseIssues.result',
    errorPath: 'viralresponseIssues.userErrors',
  };
}

export const PROFILES = gql`
  ${PROFILE_CONNECTION_FIELDS}
  ${USER_ERROR_FIELDS}
  query Profiles(
    $batchSize: Int
    $cursor: String
    $sortBy: String
    $contains: [String!]
    $filterBy: [[String!]]
    $whitelabels: [String]
  ) {
    profiles(
      first: $batchSize
      after: $cursor
      sortBy: $sortBy
      contains: $contains
      filterBy: $filterBy
      whitelabels: $whitelabels
    ) {
      result {
        ...profileConnectionFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class WhiteLabelProfilesQuery extends Query {
  static conf = {
    GQL: PROFILES,
    varNames: [
      'batchSize',
      'cursor',
      'sortBy',
      'contains',
      'filterBy',
      'whitelabels',
    ],
    resultPath: 'profiles.result',
    errorPath: 'profiles.userErrors',
  };
}

const INITIATIVES_QUERY = gql`
  ${USER_ERROR_FIELDS}
  ${INITIATIVE_CONNECTION_FIELDS}
  query Initiatives(
    $batchSize: Int
    $cursor: String
    $sortBy: String
    $contains: [String!]
    $filterBy: [[String!]]
    $whitelabels: [String]
  ) {
    initiatives(
      first: $batchSize
      after: $cursor
      sortBy: $sortBy
      contains: $contains
      filterBy: $filterBy
      whitelabels: $whitelabels
    ) {
      result {
        ...initiativeConnectionFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class WhiteLabelInitiativesQuery extends Query {
  static conf = {
    GQL: INITIATIVES_QUERY,
    varNames: [
      'batchSize',
      'cursor',
      'sortBy',
      'contains',
      'filterBy',
      'whitelabels',
    ],
    resultPath: 'initiatives.result',
    errorPath: 'initiatives.userErrors',
  };
}

const STORIES = gql`
  ${STORY_CONNECTION_FIELDS}
  ${USER_ERROR_FIELDS}
  query Stories(
    $batchSize: Int
    $cursor: String
    $sortBy: String
    $contains: [String!]
    $filterBy: [[String!]]
    $whitelabels: [String]
  ) {
    stories(
      first: $batchSize
      after: $cursor
      sortBy: $sortBy
      contains: $contains
      filterBy: $filterBy
      whitelabels: $whitelabels
    ) {
      result {
        ...storyConnectionFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;
export class WhiteLabelStoriesQuery extends Query {
  static conf = {
    GQL: STORIES,
    varNames: [
      'batchSize',
      'cursor',
      'sortBy',
      'contains',
      'filterBy',
      'whitelabels',
    ],
    resultPath: 'stories.result',
    errorPath: 'stories.userErrors',
  };
}

const ALL_THREADS_QUERY = gql`
  ${PROFILE_THREAD_CONNECTION_FIELDS}
  query Threads(
    $batchSize: Int
    $cursor: String
    $sortBy: String
    $notContains: [[String]]
    $contains: [String!]
    $filterBy: [[String!]]
    $whitelabels: [String]
  ) {
    threads(
      first: $batchSize
      after: $cursor
      sortBy: $sortBy
      contains: $contains
      notContains: $notContains
      filterBy: $filterBy
      whitelabels: $whitelabels
    ) {
      result {
        ...threadConnectionFields
      }
    }
  }
`;

export class WhiteLabelThreadsQuery extends Query {
  static conf = {
    GQL: ALL_THREADS_QUERY,
    varNames: [
      'batchSize',
      'cursor',
      'sortBy',
      'contains',
      'notContains',
      'filterBy',
      'whitelabels',
    ],
    resultPath: 'threads.result',
  };
}
